/* -------------------------- Design imports start -------------------------- */
import { Autocomplete, TextField } from '@mui/material'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import React, { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from 'react-i18next'
/* ------------------------- Functional imports end ------------------------- */

type Option<T> = {
  value: T
  label?: string
  disabled?: boolean
}

type Props<T> = {
  id?: string
  name: string
  value: T
  options: Option<T>[]
  freeSolo?: boolean
  disabled?: boolean
  required?: boolean
  validation?: boolean
  labelWidth?: number
  onChange: (value: T) => void
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function SelectWithTextInput<T extends string | number | boolean>(props: Props<T>): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const {
    id,
    name,
    value,
    options,
    freeSolo = false,
    disabled,
    onChange,
    required
  } = props
  const normalizedOptions = (items: Option<T>[]) =>
    items.map(option => ({
      ...option,
      label: option.label ?? String(option.value),
    }))
  /* --------------------------- Non state data end --------------------------- */
  const hasValue = !(value === undefined || value === null || value === '')
  const showError = Boolean(required) && !hasValue
  /* ---------------------------- Data states start --------------------------- */
  const { t } = useTranslation(['common', 'profile'])
  const [inputValue, setInputValue] = useState<string>('')
  const [newOptions, setNewOptions] = useState<Option<T>[]>(normalizedOptions(options))
  /* ----------------------------- Data states end ---------------------------- */
  /* ----------------------------- useEffects start ----------------------------- */
  useEffect(() => {
    setNewOptions(normalizedOptions(options))
  }, [options])

  useEffect(() => {
    const currentOption = newOptions.find(option => option.value === value)
    const displayLabel =
      currentOption?.label ?? (value === undefined || value === null ? '' : String(value))
    setInputValue(displayLabel)
  }, [value, newOptions])

  /* ------------------------------ useEffects end ------------------------------ */

  /* -------------------------------------------------------------------------- */
  /*                              Render component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <Autocomplete<Option<T>, false, false, boolean>
      id={id || name}
      options={newOptions}
      noOptionsText={t('common:content.label.enterForNewOption')}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.label ?? String(option.value)
      }
      getOptionDisabled={(option) => Boolean(option.disabled)}
      isOptionEqualToValue={(option, selected) => {
        if (typeof selected === 'string') {
          return option.label === selected || String(option.value) === selected
        }
        return option.value === selected.value
      }}
      value={
        newOptions.find(option => option.value === value) ??
        (inputValue === '' ? null : inputValue)
      }
      inputValue={inputValue}
      onInputChange={(_event, newValue, reason) => {
        setInputValue(newValue)
        if (reason === 'input') {
          const matchedOption = newOptions.find(option => option.label === newValue)
          onChange((matchedOption ? matchedOption.value : (newValue as unknown as T)))
        }
        if (reason === 'clear') {
          onChange('' as T)
        }
      }}
      onChange={(_event, newValue) => {
        if (typeof newValue === 'string') {
          const matchedOption = newOptions.find(option => option.label === newValue)
          const nextValue = matchedOption ? matchedOption.value : (newValue as unknown as T)
          onChange(nextValue)
          setInputValue(newValue)
          return
        }
        if (newValue) {
          onChange(newValue.value)
          setInputValue(newValue.label ?? String(newValue.value))
          return
        }
        onChange('' as T)
        setInputValue('')
      }}
      freeSolo={freeSolo}
      disabled={disabled}
      renderInput={params => (
        <TextField
          {...params}
          label={name}
          required={required}
          error={showError}
          variant="outlined"
          onKeyDown={e => {
            if (
              e.key === 'Enter' &&
              newOptions.findIndex(
                option => option.value === (inputValue as unknown as T) || option.label === inputValue
              ) === -1
            ) {
              const existingOption = newOptions.find(
                option => option.label === inputValue || String(option.value) === inputValue
              )
              if (existingOption) {
                onChange(existingOption.value)
                setInputValue(existingOption.label ?? String(existingOption.value))
                return
              }
              setNewOptions([...newOptions, { value: inputValue as unknown as T, label: inputValue }])
              onChange(inputValue as unknown as T)
            }
          }}
        />
      )}
      disablePortal /* List of suggestions will not be rendered on top of everything */
    />
  )
}
